import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { format } from "date-fns";
import "../../../css/rf.css";
import ChannelCalendar from "./ChannelCalendar";

const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";
const initial = (n) => (String(n || "?").trim()[0] || "?").toUpperCase();
const roleOf = (info) => String((info && info.role) || "").toUpperCase();

const Avatar = ({ ch, pic, big }) => {
  const src = pic || ch.profilePic;
  return <div className={`rf-avatar ${big ? "big" : ""}`}>{src ? <img src={src} alt="" /> : initial(ch.name)}</div>;
};

const TYPES = [
  { id: "text", icon: "✍️", label: "Text" },
  { id: "image", icon: "🖼", label: "Photo" },
  { id: "video", icon: "🎬", label: "Video", plan: "allowVideo" },
  { id: "audio", icon: "🎵", label: "Music", plan: "allowVideo" },
  { id: "poll", icon: "📊", label: "Poll / Question", plan: "allowPoll" },
  { id: "quiz", icon: "🧠", label: "Quiz", plan: "allowPoll" },
];
const ACCEPT = { image: "image/*", video: "video/*", audio: "audio/*,.mp3,.m4a,.aac,.ogg,.opus,.wav" };
const EMOJIS = ["😀", "😍", "🔥", "👍", "🎉", "🙏", "❤️", "😂", "✨", "📢", "💡", "✅", "⭐", "🎁", "📌", "🚀"];
const dayLabel = (t) => { const d = new Date(t), n = new Date(); const same = (a, b) => a.toDateString() === b.toDateString(); return same(d, n) ? "Today" : same(d, new Date(+n - 864e5)) ? "Yesterday" : format(d, "d MMMM yyyy"); };

const ChannelsPage = () => {
  const [view, setView] = useState("channels");
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState("");
  const [plan, setPlan] = useState({});
  const [session, setSession] = useState({});
  const [channels, setChannels] = useState([]);
  const [q, setQ] = useState("");
  const [selId, setSelId] = useState("");
  const [syncing, setSyncing] = useState(false);

  const [info, setInfo] = useState(null);
  const [msgs, setMsgs] = useState({ loading: false, supported: true, list: [], error: "" });
  const [upcoming, setUpcoming] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [drawerTab, setDrawerTab] = useState("overview");

  const [showAdd, setShowAdd] = useState(false);
  const [addLink, setAddLink] = useState("");
  const [adding, setAdding] = useState(false);

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [later, setLater] = useState(false);
  const [when, setWhen] = useState("");
  const [sending, setSending] = useState(false);
  const [type, setType] = useState("text");
  const [opts, setOpts] = useState(["", ""]);
  const [correct, setCorrect] = useState(0);
  const [reveal, setReveal] = useState(60);
  const [emoji, setEmoji] = useState(false);
  const [pics, setPics] = useState({});
  const [own, setOwn] = useState([]);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const reqRef = useRef(0);

  const loadAll = useCallback(async () => {
    try {
      const [p, s, c] = await Promise.all([
        axiosInstance.get("channels/plan-info", { timeout: 20000 }),
        axiosInstance.get("channels/session-status", { timeout: 20000 }).catch(() => ({ data: {} })),
        axiosInstance.get("channels", { timeout: 20000 }),
      ]);
      if (p.data && p.data.enabled === false) { setBlocked("Channel Scheduler is not included in your plan."); setLoading(false); return; }
      setPlan(p.data || {});
      setSession(s.data || {});
      const list = c.data.data || [];
      setChannels(list);
      setSelId((cur) => (cur && list.some((x) => x._id === cur) ? cur : list[0]?._id || ""));
      setBlocked("");
    } catch (e) {
      if (e?.response?.status === 403) setBlocked(errMsg(e)); else swal("Error", errMsg(e), "error");
    }
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const sel = channels.find((c) => c._id === selId);

  const loadChannel = useCallback(async () => {
    if (!selId) return;
    const tok = ++reqRef.current;
    const live = () => tok === reqRef.current;
    setInfo(null); setMsgs({ loading: true, supported: true, list: [], error: "" }); setUpcoming([]); setOwn([]);
    const id = selId;
    axiosInstance.get(`channels/${id}/info`).then((r) => { if (live()) setInfo(r.data.unsupported ? { ...(r.data.data || {}), unsupported: true } : r.data.data); }).catch(() => {});
    axiosInstance.get("channels/posts", { params: { channelId: id, status: "pending", limit: 10 } }).then((r) => { if (live()) setUpcoming(r.data.data || []); }).catch(() => {});
    axiosInstance.get("channels/posts", { params: { channelId: id, status: "posted", limit: 20, sort: "desc" } }).then((r) => { if (live()) setOwn((r.data.data || []).slice().reverse()); }).catch(() => {});
    try {
      const r = await axiosInstance.get(`channels/${id}/messages`, { params: { count: 30 }, timeout: 40000 });
      if (live()) setMsgs({ loading: false, supported: r.data.supported !== false, list: r.data.messages || [], error: r.data.error || "" });
    } catch (e) { if (live()) setMsgs({ loading: false, supported: false, list: [], error: errMsg(e) }); }
  }, [selId]);
  useEffect(() => { loadChannel(); }, [loadChannel]);

  // channel pictures: loaded one by one in the background so the page never waits for them
  useEffect(() => {
    let stop = false;
    (async () => {
      for (const c of channels) {
        if (stop) return;
        if (pics[c._id] !== undefined) continue;
        let url = "";
        try { const r = await axiosInstance.get(`channels/${c._id}/picture`, { timeout: 25000 }); url = r.data.dataUrl || ""; } catch (e) { url = ""; }
        if (stop) return;
        setPics((p) => ({ ...p, [c._id]: url }));
      }
    })();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [channels]);
  useEffect(() => { if (endRef.current) endRef.current.scrollTop = endRef.current.scrollHeight; }, [msgs.list, upcoming]);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await axiosInstance.post("channels/sync"); swal(data.status ? "Synced" : "Sync", data.message, data.status ? "success" : "info"); if (data.status) loadAll(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
    setSyncing(false);
  };

  const addChannel = async () => {
    if (!addLink.trim()) return;
    setAdding(true);
    try {
      const { data } = await axiosInstance.post("channels", { channelLink: addLink.trim() });
      if (data.status) { setShowAdd(false); setAddLink(""); await loadAll(); if (data.data?._id) setSelId(data.data._id); }
      else swal("Could not add", data.message, "error");
    } catch (e) { swal("Could not add", errMsg(e), "error"); }
    setAdding(false);
  };

  const removeChannel = async () => {
    const ok = await swal({ title: `Remove "${sel.name}" from the list?`, text: "It stays on WhatsApp. Its scheduled posts here are not sent.", icon: "warning", buttons: true, dangerMode: true });
    if (!ok) return;
    try { await axiosInstance.delete(`channels/${sel._id}`); setDrawer(false); setSelId(""); await loadAll(); }
    catch (e) { swal("Error", errMsg(e), "error"); }
  };

  const toggleMute = async () => {
    try { await axiosInstance.post(`channels/${sel._id}/mute`, { mute: !info.muted }); setInfo({ ...info, muted: !info.muted }); }
    catch (e) { swal("Error", errMsg(e), "error"); }
  };

  const copy = (t) => { try { navigator.clipboard.writeText(t); swal("Copied", "", "success"); } catch { swal("Copy this", t, "info"); } };

  const resetComposer = () => { setText(""); setFile(null); if (fileRef.current) fileRef.current.value = ""; setLater(false); setWhen(""); setOpts(["", ""]); setCorrect(0); setEmoji(false); };

  const send = async () => {
    if (!sel) return;
    const isPoll = type === "poll" || type === "quiz";
    const cleanOpts = opts.map((o) => o.trim()).filter(Boolean);
    if (isPoll && (!text.trim() || cleanOpts.length < 2)) return swal("Add a question", "Write the question and at least 2 answers.", "info");
    if (["image", "video", "audio"].includes(type) && !file) return swal("Choose a file", `Pick the ${type === "audio" ? "music" : type} file to post.`, "info");
    if (type === "text" && !text.trim()) return;
    let at = new Date(Date.now() + 75 * 1000);
    if (later) {
      at = new Date(when);
      if (!when || isNaN(+at) || at <= new Date()) return swal("Pick a time", "Choose a date and time in the future.", "info");
    }
    const fd = new FormData();
    fd.append("channelId", sel._id);
    fd.append("scheduledAt", at.toISOString());
    if (isPoll) {
      fd.append("mediaType", type);
      fd.append("pollQuestion", text.trim());
      fd.append("pollOptions", JSON.stringify(cleanOpts));
      if (type === "quiz") { fd.append("quizCorrect", String(Math.min(correct, cleanOpts.length - 1))); fd.append("quizRevealMinutes", String(reveal)); }
    } else {
      fd.append("text", type === "audio" ? "" : text);
      if (file) { fd.append("mediaType", type === "text" ? "image" : type); fd.append("media", file); }
    }
    setSending(true);
    try {
      const { data } = await axiosInstance.post("channels/posts", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      if (data.status) { resetComposer(); loadChannel(); swal("Done", data.message || "Scheduled", "success"); }
      else swal("Not sent", data.message, "error");
    } catch (e) { swal("Not sent", errMsg(e), "error"); }
    setSending(false);
  };

  if (loading) return <div className="rf text-center py-5"><Spinner animation="border" variant="success" /></div>;
  if (blocked) return (
    <div className="rf"><div className="rf-card rf-empty"><div className="ico">🔒</div><h5>Channels are not available</h5><p>{blocked}</p>
      <Link to="/packs" className="btn-rf d-inline-block">Upgrade plan</Link></div></div>
  );

  const connected = session.status === "connected";
  const visible = channels.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));
  const role = roleOf(info);

  return (
    <div className="rf">
      <div className="rf-head">
        <div className="rf-head-icon">📢</div>
        <div><h3>Channels</h3><p>Publish to your WhatsApp channels, now or on a schedule.</p></div>
        <div className="rf-spacer" />
        <div className="rf-seg">
          <button className={view === "channels" ? "on" : ""} onClick={() => setView("channels")}>Channels</button>
          <button className={view === "calendar" ? "on" : ""} onClick={() => setView("calendar")}>Calendar</button>
        </div>
        <button className="btn-rf-o" onClick={() => setShowAdd(true)}>+ Add channel</button>
        {session.useMainDevice && <button className="btn-rf-o" disabled={syncing || !connected} onClick={sync}>{syncing ? "Syncing…" : "Sync"}</button>}
        <Link to="/channel-classic" className="btn-rf-o" title="Polls, recurring posts, bulk upload">Classic view</Link>
        {plan.allowAutopilot && <Link to="/flows" className="btn-rf">Create flow</Link>}
      </div>

      {!connected && (
        <div className="rf-alert amber d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <span>No WhatsApp device is connected, so posts cannot go out.</span><div className="rf-spacer" />
          <Link to="/device" className="btn-rf-o">Connect a device</Link>
        </div>
      )}

      {view === "calendar" ? (
        <ChannelCalendar channels={channels} withFlows={!!plan.allowAutopilot} initialChannelId={selId} />
      ) : channels.length === 0 ? (
        <div className="rf-card rf-empty">
          <div className="ico">📢</div><h5>No channels yet</h5>
          <p>Add a channel with its invite link{session.useMainDevice ? ", or sync the channels you own" : ""}.</p>
          <button className="btn-rf" onClick={() => setShowAdd(true)}>Add your first channel</button>
        </div>
      ) : (
        <div className="rf-split">
          <div className="rf-list">
            <div className="rf-list-search"><input className="form-control" placeholder="Search channels" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="rf-list-body">
              <div className="rf-list-group"><span className={`rf-dot ${connected ? "on" : ""}`} />{session.phone ? `+${String(session.phone).replace(/^\+/, "")}` : "Your number"} · {connected ? "Connected" : "Not connected"}</div>
              {visible.map((c) => (
                <div key={c._id} className={`rf-ch ${c._id === selId ? "sel" : ""}`} onClick={() => setSelId(c._id)}>
                  <Avatar ch={c} pic={pics[c._id]} />
                  <div style={{ minWidth: 0 }}>
                    <div className="nm">{c.name}</div>
                    <div className="sub">{c.postsThisMonth || 0} posts this month{c.postsToday ? ` · ${c.postsToday} today` : ""}</div>
                  </div>
                </div>
              ))}
              {visible.length === 0 && <div className="rf-help p-3">No channel matches.</div>}
            </div>
          </div>

          {sel && (
            <div className="rf-view">
              <div className="rf-view-head">
                <Avatar ch={sel} pic={pics[sel._id] || (info && info.pictureUrl ? "" : "")} big />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{sel.name} {role === "OWNER" && <span className="rf-chip green">Owner</span>}{role === "ADMIN" && <span className="rf-chip">Admin</span>}{role === "SUBSCRIBER" && <span className="rf-chip">Following</span>}</div>
                  <div className="rf-meta"><span className={`rf-dot ${connected ? "on" : ""}`} /> {connected ? "Live" : "Offline"}{info && info.followers ? ` · ${info.followers.toLocaleString()} followers` : ""}{info && info.muted ? " · Muted" : ""}</div>
                </div>
                <div className="rf-spacer" />
                <button className="btn-rf-o" onClick={loadChannel} title="Refresh">↻</button>
                <button className="btn-rf-o" onClick={() => { setDrawerTab("overview"); setDrawer(true); }}>Info</button>
              </div>

              <div className="rf-msgs" ref={endRef}>
                {msgs.loading && <div className="text-center"><Spinner animation="border" size="sm" variant="success" /> <span className="rf-help">Loading posts…</span></div>}
                {(() => {
                  const fromWA = msgs.list.length > 0;
                  const items = fromWA ? msgs.list : own.map((p) => ({ id: p._id, at: new Date(p.scheduledAt).getTime(), text: p.pollData && p.pollData.question ? p.pollData.question : p.text, kind: p.pollData && p.pollData.question ? "poll" : p.mediaFiles && p.mediaFiles[0] ? p.mediaFiles[0].type : "text", views: 0, reactions: [], mine: true }));
                  let last = "";
                  return (
                    <>
                      {!msgs.loading && !fromWA && (
                        <div className="rf-alert amber">
                          {msgs.supported ? "WhatsApp returned no history for this channel." : `Channel history is unavailable right now${msgs.error ? ` (${msgs.error})` : ""}.`}
                          {items.length ? " Showing the posts sent from here instead." : " You can still post and schedule below."}
                        </div>
                      )}
                      {items.map((m) => {
                        const day = m.at ? dayLabel(m.at) : "";
                        const sep = day && day !== last ? <div className="rf-daysep" key={`d${m.id}`}><span>{day}</span></div> : null;
                        last = day || last;
                        return (
                          <React.Fragment key={m.id || m.at}>
                            {sep}
                            <div className="rf-bubble">
                              {m.kind !== "text" && <div className="rf-meta">{m.kind === "image" ? "🖼 Photo" : m.kind === "video" ? "🎬 Video" : m.kind === "poll" ? "📊 Poll" : m.kind === "audio" ? "🎵 Music" : m.kind === "document" ? "📄 Document" : "Message"}</div>}
                              {m.text || (m.kind === "text" ? "(empty)" : "")}
                              <div className="meta"><span>{m.at ? format(new Date(m.at), "HH:mm") : ""}</span>{m.mine && <span>✓ sent from here</span>}{m.views > 0 && <span>👁 {m.views}</span>}{(m.reactions || []).map((r, i) => <span key={i}>{r.code} {r.count}</span>)}</div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </>
                  );
                })()}
                {upcoming.map((p) => (
                  <div className="rf-bubble sched" key={p._id}>
                    {p.pollData && p.pollData.question ? `📊 ${p.pollData.question}` : p.mediaFiles && p.mediaFiles[0] && !p.text ? `(${p.mediaFiles[0].type} post)` : p.text || "(post)"}
                    <div className="meta"><span>🕒 Scheduled · {format(new Date(p.scheduledAt), "d MMM, HH:mm")}</span></div>
                  </div>
                ))}
              </div>

              {role === "SUBSCRIBER" ? (
                <div className="rf-composer"><span className="rf-help">You follow this channel. Only its admins can post.</span></div>
              ) : (
                <div className="rf-composer">
                  <div className="rf-types">
                    {TYPES.map((t) => {
                      const locked = t.plan && plan[t.plan] === false;
                      return <button key={t.id} className={`${type === t.id ? "on" : ""}`} disabled={locked} title={locked ? "Not included in your plan" : ""} onClick={() => { setType(t.id); setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>{t.icon} {t.label}{locked ? " 🔒" : ""}</button>;
                    })}
                  </div>
                  <textarea rows={2} placeholder={type === "poll" || type === "quiz" ? "Type your question" : type === "audio" ? "Music posts have no caption" : "Write an update for your followers"} disabled={type === "audio"} value={text} onChange={(e) => setText(e.target.value)} />
                  {(type === "poll" || type === "quiz") && (
                    <div className="mt-2">
                      {opts.map((o, i) => (
                        <div className="d-flex align-items-center mb-1" style={{ gap: 6 }} key={i}>
                          {type === "quiz" && <input type="radio" name="correct" checked={correct === i} onChange={() => setCorrect(i)} title="Correct answer" />}
                          <input className="form-control" placeholder={`Answer ${i + 1}`} value={o} onChange={(e) => setOpts(opts.map((x, j) => (j === i ? e.target.value : x)))} />
                          {opts.length > 2 && <button className="btn-rf-o" onClick={() => { setOpts(opts.filter((_, j) => j !== i)); if (correct >= opts.length - 1) setCorrect(0); }}>✕</button>}
                        </div>
                      ))}
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                        {opts.length < 6 && <button className="btn-rf-o" onClick={() => setOpts([...opts, ""])}>+ Answer</button>}
                        {type === "quiz" && <><span className="rf-help mb-0">Reveal the answer after</span><select className="form-control" style={{ maxWidth: 140 }} value={reveal} onChange={(e) => setReveal(Number(e.target.value))}>{[[15, "15 min"], [60, "1 hour"], [180, "3 hours"], [720, "12 hours"], [1440, "24 hours"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></>}
                      </div>
                      {type === "quiz" && <div className="rf-help">Pick the correct answer with the dot. WhatsApp channels have no built-in quiz, so the quiz is a poll and the answer is posted as a follow-up.</div>}
                    </div>
                  )}
                  <div className="row2">
                    {ACCEPT[type] && (
                      <>
                        <input ref={fileRef} type="file" accept={ACCEPT[type]} style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0] || null)} />
                        <button className="btn-rf-o" onClick={() => fileRef.current && fileRef.current.click()}>📎 {file ? file.name.slice(0, 22) : `Choose ${type === "audio" ? "music" : type}`}</button>
                        {file && <button className="btn-rf-o" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>✕</button>}
                      </>
                    )}
                    {(type === "text" || type === "image" || type === "video") && (
                      <span style={{ position: "relative" }}>
                        <button className="btn-rf-o" onClick={() => setEmoji(!emoji)}>😊</button>
                        {emoji && <div className="rf-emoji">{EMOJIS.map((e) => <button key={e} onClick={() => { setText(text + e); setEmoji(false); }}>{e}</button>)}</div>}
                      </span>
                    )}
                    <label className="mb-0 d-flex align-items-center" style={{ gap: 6 }}><input type="checkbox" checked={later} onChange={(e) => setLater(e.target.checked)} /> Schedule</label>
                    {later && <input type="datetime-local" className="form-control" style={{ maxWidth: 220 }} value={when} onChange={(e) => setWhen(e.target.value)} />}
                    <div className="rf-spacer" />
                    <button className="btn-rf" disabled={sending} onClick={send}>{sending ? "Sending…" : later ? "Schedule" : "Send"}</button>
                  </div>
                  {!later && <div className="rf-help">“Send” goes out within about a minute.</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {drawer && sel && (
        <div className="rf-drawer">
          <div className="d-flex align-items-center mb-3" style={{ gap: 10 }}>
            <Avatar ch={sel} pic={pics[sel._id]} big /><div><div style={{ fontWeight: 600 }}>{sel.name}</div><div className="rf-meta">{info && info.followers ? `${info.followers.toLocaleString()} followers` : "Channel info"}</div></div>
            <div className="rf-spacer" /><button className="btn-rf-o" onClick={() => setDrawer(false)}>✕</button>
          </div>
          <div className="rf-tabs">
            <button className={drawerTab === "overview" ? "on" : ""} onClick={() => setDrawerTab("overview")}>Overview</button>
            <button className={drawerTab === "manage" ? "on" : ""} onClick={() => setDrawerTab("manage")}>Manage</button>
          </div>
          {!info && <div className="text-center"><Spinner animation="border" size="sm" variant="success" /></div>}
          {info && info.unsupported && <div className="rf-alert amber">WhatsApp details could not be loaded right now.</div>}
          {info && drawerTab === "overview" && (
            <>
              {info.description && <p>{info.description}</p>}
              <div className="rf-row-act"><span className="rf-label">Your role</span><span>{role || "—"}</span></div>
              <div className="rf-row-act"><span className="rf-label">Followers</span><span>{info.followers ? info.followers.toLocaleString() : "—"}</span></div>
              <div className="rf-row-act"><span className="rf-label">Created</span><span>{info.createdAt ? format(new Date(info.createdAt), "d MMM yyyy") : "—"}</span></div>
              <div className="rf-row-act"><span className="rf-label">Posts this month</span><span>{sel.postsThisMonth || 0}</span></div>
              {info.invite && (
                <div className="d-flex mt-3" style={{ gap: 8 }}>
                  <button className="btn-rf-o" onClick={() => copy(`https://whatsapp.com/channel/${info.invite}`)}>Copy link</button>
                  <a className="btn-rf-o" href={`https://whatsapp.com/channel/${info.invite}`} target="_blank" rel="noopener noreferrer">Open</a>
                </div>
              )}
            </>
          )}
          {info && drawerTab === "manage" && (
            <>
              <div className="rf-row-act"><div><b>Mute notifications</b><div className="rf-help">On the linked phone.</div></div><button className="btn-rf-o" disabled={info.unsupported} onClick={toggleMute}>{info.muted ? "Unmute" : "Mute"}</button></div>
              {plan.allowAutopilot && <div className="rf-row-act"><div><b>Automate this channel</b><div className="rf-help">Create a flow that posts here for you.</div></div><Link className="btn-rf-o" to="/flows">Flows</Link></div>}
              <div className="rf-row-act"><div><b>Remove from list</b><div className="rf-help">Stays on WhatsApp.</div></div><button className="btn-rf-danger" onClick={removeChannel}>Remove</button></div>
            </>
          )}
        </div>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add channel</Modal.Title></Modal.Header>
        <Modal.Body className="rf">
          <label className="rf-label">Number</label>
          <input className="form-control mb-1" disabled value={session.phone ? `+${String(session.phone).replace(/^\+/, "")}` : "Your connected device"} readOnly />
          <div className="rf-help mb-3">The connected number looks the channel up on WhatsApp.</div>
          <label className="rf-label">Invite link or channel ID</label>
          <input className="form-control" placeholder="https://whatsapp.com/channel/…" value={addLink} onChange={(e) => setAddLink(e.target.value)} />
        </Modal.Body>
        <Modal.Footer className="rf">
          <button className="btn-rf-o" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn-rf" disabled={adding || !addLink.trim()} onClick={addChannel}>{adding ? "Finding…" : "Find channel"}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChannelsPage;
